import React, { type ReactElement, useState } from 'react'
import ThemeInputDialog from 'common/ui/theme-input-dialog/ThemeInputDialog'
import { FormControlLabel, FormGroup, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { type LookupItem, LookupTypes } from 'features/lookup/lookup.types'
import ThemeCheckbox from 'common/ui/theme-checkbox'
import { editVendorIndustries } from 'features/vendor/vendor.service'

import styles from './IndustriesDialog.module.scss'

interface IndustriesDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const IndustriesDialog: React.FC<IndustriesDialogProps> = ({ isOpen, setIsOpen }): ReactElement => {
  const dispatch = useAppDispatch()
  const { vendor, vendorIndustriesStatus } = useAppSelector((state) => state.vendor)
  const { lookup } = useAppSelector((state) => state.lookup)
  const [selectedIndustries, setSelectedIndustries] = useState<LookupItem[]>(vendor?.industries ?? [])
  const initialState: LookupItem[] = vendor?.industries ?? []

  const areEqual = (): boolean => {
    const idsInitialState = initialState
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .toString()
    const idsSelectedIndustries = selectedIndustries
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .toString()

    return idsInitialState === idsSelectedIndustries
  }

  const handleVendorUpdate = async (): Promise<void> => {
    await dispatch(editVendorIndustries({ industries: selectedIndustries }))
    setIsOpen(false)
  }

  const handleCheckboxChange = (industry: LookupItem): void => {
    if (selectedIndustries.some((item) => item.id === industry.id)) {
      setSelectedIndustries(selectedIndustries.filter((item) => item.id !== industry.id))
    } else {
      setSelectedIndustries([...selectedIndustries, industry])
    }
  }

  return (
    <ThemeInputDialog
      open={isOpen}
      title="Industrije"
      description="Imate mogućnost da izaberete maksimum 6 industrija za koje imate najviše iskustva. Ove informacije su informativne i ne morate ih popuniti da bi učestvovali na tenderima."
      status={vendorIndustriesStatus}
      onClose={() => {
        setIsOpen(false)
      }}
      confirmButtonText="snimi"
      declineButtonText="izađi"
      confirmButtonAction={() => {
        void handleVendorUpdate()
      }}
      confirmButtonDisabled={areEqual()}
      PaperProps={{
        sx: {
          width: '850px',
          height: '640px',
        },
      }}
    >
      <form className={styles.container}>
        <div className={styles.scrollableWrapper}>
          <div className={styles.scrollable}>
            <FormGroup>
              {lookup[LookupTypes.industry]?.map((industry) => (
                <FormControlLabel
                  key={industry.id}
                  className={styles.increasedGap}
                  control={
                    <ThemeCheckbox
                      className={styles.item}
                      checked={selectedIndustries.some((item) => item.id === industry.id)}
                      disabled={
                        selectedIndustries?.length >= 6 &&
                        !selectedIndustries.some((item) => item.id === industry.id)
                      }
                      onChange={() => {
                        handleCheckboxChange(industry)
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ marginLeft: '10px' }} variant="title3">
                      {industry.name}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </div>
        </div>
      </form>
    </ThemeInputDialog>
  )
}

export default IndustriesDialog
